import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { navigate } from '../../../common/js/utils/CommonUtils';
import { authSlice } from '../../../api/actions/authApi';
import {
  useGetCompanyDetailsMutation,
  useGetTenantPreferenceMutation,
  useGetPermissionDetailsMutation,
  useAddTrialPlanMutation,
  setCompanyData,
  companyInfoReducer,
  useUpdateOnboardStatusMutation,
} from '../../../api/actions/companyApi';
import withReducer from '../../../common/js/utils/WithReducer';
import Config from '../../../common/js/utils/Config';
import Debug from '../../../common/js/utils/Debug';
import PageSpinner from '../../../common/js/components/PageSpinner';
import ErrorScreenAddTrialPlan from '../components/ErrorScreenAddTrialPlan';
import { setLanguage, languageReducer } from 'app/api/actions/languageSlice';

const AccountLinked = (props) => {
  const [updateOnboardStatus] = useUpdateOnboardStatusMutation();
  const [step] = useState(
    useSelector((state) => {
      return state.tpUserOrgs.meta.step;
    }),
  );
  let companyData = useSelector((state) => {
    return state.companyInfo ? state.companyInfo.companyData : {};
  });

  const [compData, setCompData] = useState(companyData);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [triggerActivateTrial, setTriggerActivateTrial] = useState(false);
  const [hasPerm, setHasPerm] = useState(false);

  let userData = useSelector((state) => {
    try {
      return state.auth.user.tpInfo;
    } catch (e) {
      Debug.log(e);
      return {};
    }
  });

  const [companyDetailsRequest] = useGetCompanyDetailsMutation();
  const [tenantPreferenceRequest] = useGetTenantPreferenceMutation();
  const [permissionDetailsRequest] = useGetPermissionDetailsMutation();
  const [addTrialPlan] = useAddTrialPlanMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (triggerActivateTrial) {
      setTriggerActivateTrial(false);
      activateTrial()
        .then((isSuccess) => {
          Debug.log('in .then activate trial isSuccess' + isSuccess);
          if (isSuccess) {
            continueOnboarding();
          } else {
            setShowErrorScreen(true);
          }
        })
        .catch((err) => {
          Debug.log('in catch activate trial .then' + err.message);
          setShowErrorScreen(true);
        });
    }
  }, [triggerActivateTrial]);

  useEffect(() => {
    if (userData.email) {
      getCompanyDetails(userData.tenant);
    }
  }, [userData.email]);

  const getCompanyDetails = (tenantId) => {
    Promise.all([companyDetailsRequest({ tenantId }), tenantPreferenceRequest({ tenantId }), permissionDetailsRequest()])
      .then((res) => {
        const [companyRes, tenantPrefRes, permissionsRes] = res;
        let company = companyRes.data;
        let tenantPreferences = tenantPrefRes.data || [];
        let permissions = permissionsRes.data ? permissionsRes.data.permissions || [] : [];
        let _companyData = {
          ...companyData,
          ...company,
        };
        try {
          let info = tenantPreferences.find((p) => {
            return p.key == 'enforce_azure_login';
          });
          let forceAadLoginPref = info ? info.value == 'true' : false;
          _companyData.forceAadLogin = forceAadLoginPref;
        } catch (e) {
          Debug.log('Exception in fetch preference', e);
        }

        let _hasPerm = permissions.includes('manage__company_onboarding');
        setHasPerm(_hasPerm);
        setCompData(_companyData);
        const language =
          _companyData.owner && _companyData.owner.locale
            ? _companyData.owner.locale
            : (navigator.language && navigator.language.split('-')[0]) || 'en';
        dispatch(setLanguage({ language, updateLocalStorage: false }));
        let _onboardStatus =
          _companyData.onboardStatus && _companyData.onboardStatus.length > 0 ? _companyData.onboardStatus[0] : '';
        // Update the onboarding status.
        let targetOnboardStatus = localStorage.getItem('target_onboarding_status');
        if (
          targetOnboardStatus &&
          _onboardStatus &&
          _onboardStatus !== 'PARTNER_ASSIGNMENT' &&
          _onboardStatus !== targetOnboardStatus
        ) {
          updateOnboardStatus({ tenant: _companyData.id, status: targetOnboardStatus })
            .then(() => {
              _companyData.onboardStatus = [targetOnboardStatus];
              localStorage.removeItem('target_onboarding_status');
              setCompData(_companyData);
              setTriggerActivateTrial(true);
            })
            .catch(() => {
              localStorage.removeItem('target_onboarding_status');
              setTriggerActivateTrial(true);
            });
          return;
        } else if (_onboardStatus && ['IT_ADMINISTRATOR', 'COMPANY_PREVIEW'].indexOf(_onboardStatus) >= 0 && !_hasPerm) {
          Config.redirectToUrl(window.location.origin + '/ui/error/onboardingPending');
          return;
        }
        setTriggerActivateTrial(true);
      })
      .catch((err) => {
        Debug.log(err);
      });
  };

  const activateTrial = () => {
    return new Promise((resolve) => {
      let service = localStorage.getItem('selected_service');
      let serviceName = localStorage.getItem('service_name');
      if (!service || !serviceName) {
        // Update plan is not supported/needed for this service. Continue as before.
        resolve(true);
      } else if (!hasPerm) {
        // Update plan is supported, but current user doesn't have permission. Redirect to error page, regardless of onboard status.
        Config.redirectToUrl(window.location.origin + '/ui/error/unauthorizeForTrialPlan');
        return;
      } else {
        // API call to add trial plan
        let paramData = {
          tenantId: userData.tenant,
          planName: service,
        };
        Debug.log('Calling Add api trial flow plan');
        addTrialPlan(paramData)
          .then((res) => {
            Debug.log('ADD TRIAL PLAN API RESPONSE');
            console.log(res);
            if (res.data && res.data.startDate) {
              localStorage.setItem('service_added', service);
              resolve(true); // trial plan added for the user
              return;
            } else if (res.error) {
              if (res.error.originalStatus === 409) {
                localStorage.setItem('service_added', service);
                Debug.log('Failed to add trial plan, plan already exists' + res.error.data);
                resolve(true);
                return;
              }
              Debug.log('Failed to add trial plan' + res.error.data);
              resolve(false); // failed to add trial plan
            }
            resolve(false);
          })
          .catch((err) => {
            Debug.log('Failed to add trial plan' + err.message);
            resolve(false);
          });
      }
    });
  };

  const continueOnboarding = () => {
    let redirectPath = localStorage.getItem('onboarding_redirect_target') || '';
    let service = localStorage.getItem('selected_service');
    try {
      sessionStorage.removeItem('new_user_emailId');
      if (compData.onboardStatus && compData.onboardStatus.length > 0) {
        dispatch(setCompanyData(compData));
        const language =
          compData.owner && compData.owner.locale
            ? compData.owner.locale
            : (navigator.language && navigator.language.split('-')[0]) || 'en';
        dispatch(setLanguage({ language, updateLocalStorage: false }));
        let _onboardStatus = compData.onboardStatus[0];
        if (_onboardStatus && ['IT_ADMINISTRATOR', 'COMPANY_PREVIEW'].indexOf(_onboardStatus) < 0) {
          Config.redirectToUrl(Config.getRedirectUrlOnSkip());
          return;
        } else if (_onboardStatus && !hasPerm) {
          Config.redirectToUrl(window.location.origin + '/ui/error/onboardingPending');
          return;
        }
        if (redirectPath) {
          navigate(props.history, redirectPath);
          localStorage.removeItem('onboarding_redirect_target');
          return;
        } else if (step) {
          let _step = step;
          if (step == 'start' && _onboardStatus == 'COMPANY_PREVIEW') {
            _step = 'company';
          }
          let objPath = {
            company: 'company-create',
          };
          if (objPath[_step]) {
            navigate(props.history, objPath[_step]);
            return;
          }
        }
      } else {
        Config.redirectToUrl(Config.getRedirectUrlOnSkip());
        return;
      }
    } catch (e) {
      Debug.log('catch continue onboarding' + e.message);
    }
  };

  const handleOnboardingCallback = () => {
    let redirectPath = localStorage.getItem('onboarding_redirect_target') || '';
    setShowErrorScreen(false);
    localStorage.removeItem('selected_service');
    continueOnboarding(redirectPath);
  };

  if (showErrorScreen) {
    return <ErrorScreenAddTrialPlan callOnboardingCallBack={handleOnboardingCallback} />;
  }

  return <PageSpinner />;
};

export default withReducer({
  [authSlice.name]: authSlice.reducer,
  [companyInfoReducer.name]: companyInfoReducer.reducer,
  [languageReducer.name]: languageReducer.reducer,
})(AccountLinked);
